import type { RawMessage } from "@/api/messages";
import {
  createBillingCheckoutSession,
  fetchBillingCheckoutStatus,
  fetchBillingPaymentMethod,
  toggleSaveBilling,
} from "@/api/billing";
import { trackMetric } from "@/api/metrics";
import {
  clearSaveBillingChoice,
  saveBillingChoiceEnabled,
  storeSaveBillingChoice,
} from "@/checkout/saveBillingChoice";
import { Button } from "@penmate/react-shared/ui/button";
import {
  DocumentDialog,
  DocumentDialogClose,
  DocumentDialogContent,
  DocumentDialogDescription,
  DocumentDialogTitle,
} from "@/components/ui/document-dialog";
import { loadStripe } from "@stripe/stripe-js";
import { RefreshCwIcon } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useAutoAnimate } from "@formkit/auto-animate/react";
import { useQueryClient } from "@tanstack/react-query";

type EmbeddedCheckoutInstance = { mount: (selectorOrElement: string | HTMLElement) => void; destroy: () => void };

const getStripeKey = (): string => {
  const w = window as any;
  const key = w?.Penmate?.env?.stripeKey;
  if (typeof key !== "string" || !key.trim()) throw new Error("Penmate.env.stripeKey is not available");
  return key;
};

const getExistingPayment = (letter: RawMessage | null): any | null => {
  const v = (letter as any)?.payment ?? (letter as any)?.data?.payment;
  return v && typeof v === "object" ? v : null;
};

const formatPaymentSummary = (payment: any | null): string => {
  if (!payment || typeof payment !== "object") return "";
  const brand = typeof payment.brand === "string" ? payment.brand.toLowerCase() : "";
  if (brand === "cashapp") return "Cash App Pay";
  const last4 = typeof payment.last4 === "string" ? payment.last4 : "";
  const brandLabel = brand ? brand[0].toUpperCase() + brand.slice(1) : "Card";
  return last4 ? `${brandLabel} •••• ${last4}` : brandLabel;
};

export function PaymentSheet(props: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  checkoutLetter: RawMessage | null;
  onSave: (args: { payment: any }) => Promise<void>;
  saveBillingActive?: boolean;
  // Fired only when the customer closes the sheet themselves (Cancel,
  // overlay, Escape) — never on the post-attach close.
  onDismiss?: () => void;
}) {
  const { open, onOpenChange, checkoutLetter, onSave, saveBillingActive, onDismiss } = props;
  const [contentAutoAnimateRef] = useAutoAnimate<HTMLDivElement>();
  const queryClient = useQueryClient();

  const existingPayment = useMemo(() => getExistingPayment(checkoutLetter), [checkoutLetter]);
  const existingSummary = useMemo(() => formatPaymentSummary(existingPayment), [existingPayment]);
  const checkoutLetterId = useMemo(() => {
    const id = (checkoutLetter as any)?.id ?? (checkoutLetter as any)?.data?.id ?? null;
    return typeof id === "number" || typeof id === "string" ? String(id) : null;
  }, [checkoutLetter]);

  const [showCheckout, setShowCheckout] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isBusy, setIsBusy] = useState(false);
  const [embeddedMounted, setEmbeddedMounted] = useState(false);

  // Save-billing choice (slice 2.6). Entry mode: checkbox in the fixed
  // bottom chrome, default ON at every sheet open — pure legacy behavior.
  // The ref mirrors the state because Stripe's onComplete closes over the
  // mount-time finalizeSession.
  const [saveCard, setSaveCard] = useState(true);
  const saveCardRef = useRef(true);
  // Summary mode: null = untouched this open (follow the server-derived
  // prop); "removed"/"kept" = the customer's in-sheet choice governs.
  const [summaryChoice, setSummaryChoice] = useState<null | "removed" | "kept">(null);
  const [summaryBusy, setSummaryBusy] = useState(false);

  const contentHeightClass = showCheckout
    ? "h-[calc(100vh-2rem)] supports-[height:100dvh]:h-[calc(100dvh-2rem)]"
    : "h-auto";

  const mountRef = useRef<HTMLDivElement | null>(null);
  const embeddedRef = useRef<EmbeddedCheckoutInstance | null>(null);
  const sessionIdRef = useRef<string | null>(null);

  const destroyEmbedded = () => {
    const inst = embeddedRef.current;
    if (inst) {
      try {
        inst.destroy();
      } catch {
        // ignore
      }
      embeddedRef.current = null;
    }
    sessionIdRef.current = null;
    setEmbeddedMounted(false);
  };

  const finalizeSession = async (sessionId: string) => {
    setLoadError(null);
    setIsBusy(true);
    try {
      const status = await fetchBillingCheckoutStatus({ sessionId });
      const setupIntent = status.setupIntent;
      const paymentMethodId =
        setupIntent && typeof setupIntent.payment_method === "string" ? setupIntent.payment_method : null;
      if (!paymentMethodId) throw new Error("Payment method not found");
      const details = await fetchBillingPaymentMethod({ id: paymentMethodId });
      const payment = {
        id: paymentMethodId,
        paymentIntentId: setupIntent?.id ?? status.setup_intent_id ?? null,
        paymentIntentStatus: setupIntent?.status ?? null,
        ...details,
      };
      // Capture the choice before the draft save: onSave's cache
      // invalidation re-runs the open-effect, which resets the ref.
      const save = saveCardRef.current;
      await onSave({ payment });
      if (saveBillingChoiceEnabled()) {
        let applied = false;
        try {
          await toggleSaveBilling({ save });
          applied = true;
          void queryClient.invalidateQueries({ queryKey: ["pmreact", "auth", "validate-token"] });
        } catch {
          // Best-effort: the attach already landed; a failed flag write must
          // not fail checkout.
        }
        void trackMetric("save_billing_choice", {
          letter_id: checkoutLetterId,
          save,
          applied,
          context: "attach",
        });
        clearSaveBillingChoice(sessionId);
      }
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : String(e));
    } finally {
      setIsBusy(false);
    }
  };

  // Summary-mode opt-out (slice 2.6): immediate toggle, no dialog. The
  // server also drops the Stripe default on false (legacy parity); Undo
  // restores the flag, and the default re-stamps on the next successful
  // send via authorize_payment. The current letter's attachment is
  // untouched either way.
  const setSummarySave = async (save: boolean) => {
    if (summaryBusy) return;
    setSummaryBusy(true);
    try {
      await toggleSaveBilling({ save });
      setSummaryChoice(save ? "kept" : "removed");
      void trackMetric("save_billing_choice", {
        letter_id: checkoutLetterId,
        save,
        applied: true,
        context: "summary",
      });
      void queryClient.invalidateQueries({ queryKey: ["pmreact", "auth", "validate-token"] });
    } catch {
      // Leave the line as it was; the tap can simply be retried.
    } finally {
      setSummaryBusy(false);
    }
  };

  const saveChoiceUiEnabled = saveBillingChoiceEnabled();
  const summarySaved = summaryChoice === null ? saveBillingActive === true : summaryChoice === "kept";

  useEffect(() => {
    if (!open) {
      destroyEmbedded();
      setLoadError(null);
      setIsBusy(false);
      setShowCheckout(false);
      setEmbeddedMounted(false);
      return;
    }

    // Default behavior: if the user already has a payment method on the draft, show a summary first.
    setShowCheckout(!existingPayment);
    setEmbeddedMounted(false);
    setSaveCard(true);
    saveCardRef.current = true;
    setSummaryChoice(null);
    setSummaryBusy(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, (existingPayment as any)?.id]);

  useEffect(() => {
    if (!open) return;
    if (!showCheckout) return;
    if (!mountRef.current) return;
    if (embeddedRef.current) return;

    let cancelled = false;

    void (async () => {
      setLoadError(null);
      setIsBusy(true);
      setEmbeddedMounted(false);

      try {
        const stripeKey = getStripeKey();
        const stripe = await loadStripe(stripeKey);
        if (!stripe || typeof (stripe as any).initEmbeddedCheckout !== "function") {
          throw new Error("Stripe embedded checkout is not available");
        }

        const returnUrl = (() => {
          const url = new URL(window.location.href);
          url.hash = "";
          // Always return to the Angular-mounted React Messenger route so we can
          // reliably resume after Cash App redirects (Angular otherwise redirects deep links).
          const m = url.pathname.match(/\/penmates\/([^/]+)\//i);
          if (m && m[1]) {
            url.pathname = `/penmates/${m[1]}/messenger`;
          }
          if (checkoutLetterId) {
            url.searchParams.set("messageId", checkoutLetterId);
            url.searchParams.set("letterId", checkoutLetterId);
          }
          // Prefer a URL-provided session id when Stripe supports the placeholder.
          // Stripe only replaces the literal token `{CHECKOUT_SESSION_ID}`; it will NOT
          // replace the URL-encoded form (`%7BCHECKOUT_SESSION_ID%7D`).
          url.searchParams.set("session_id", "{CHECKOUT_SESSION_ID}");
          return url
            .toString()
            .replace(/session_id=%7BCHECKOUT_SESSION_ID%7D/i, "session_id={CHECKOUT_SESSION_ID}");
        })();
        const session = await createBillingCheckoutSession({ returnUrl });
        if (cancelled) return;

        sessionIdRef.current = session.id;
        // Persist the checkbox value so a redirect-based attach (Cash App)
        // can apply it during resume; kept in sync on every toggle below.
        if (saveBillingChoiceEnabled()) storeSaveBillingChoice(session.id, saveCardRef.current);
        const embedded = (await (stripe as any).initEmbeddedCheckout({
          clientSecret: session.client_secret,
          onComplete: async () => {
            if (cancelled) return;
            const sessionId = sessionIdRef.current;
            if (!sessionId) return;
            await finalizeSession(sessionId);
            onOpenChange(false);
          },
        })) as EmbeddedCheckoutInstance;

        if (cancelled) {
          try {
            embedded.destroy();
          } catch {
            // ignore
          }
          return;
        }

        embeddedRef.current = embedded;
        embedded.mount(mountRef.current);
        setEmbeddedMounted(true);
      } catch (e) {
        setLoadError(e instanceof Error ? e.message : String(e));
      } finally {
        setIsBusy(false);
      }
    })();

    return () => {
      cancelled = true;
      destroyEmbedded();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, showCheckout]);

  return (
    // Disable Radix scroll-lock: it can prevent scrolling in fixed/fullscreen overlays on mobile WebViews.
    <DocumentDialog
      open={open}
      onOpenChange={(next) => {
        if (!next) destroyEmbedded();
        onOpenChange(next);
        // Stripe's onComplete closes via the onOpenChange prop directly and
        // never lands here, so this path is a customer dismissal by
        // construction.
        if (!next && !isBusy) onDismiss?.();
      }}
      modal={false}
    >
      <DocumentDialogContent
        className={`w-[calc(100vw-1rem)] max-w-md !p-0 ${contentHeightClass} max-h-[calc(100vh-2rem)] supports-[height:100dvh]:max-h-[calc(100dvh-2rem)] overflow-hidden sm:h-auto sm:max-h-[calc(100vh-2rem)]`}
      >
        <DocumentDialogTitle className="sr-only">Payment</DocumentDialogTitle>
        <DocumentDialogDescription className="sr-only">Add a payment method to pay for this message.</DocumentDialogDescription>

        <div className={`flex min-h-0 flex-col ${showCheckout ? "h-full" : ""}`}>
          <div className="flex items-center justify-between border-b px-4 py-3">
            <div className="min-w-0">
              <div className="text-sm font-semibold">Payment</div>
              <div className="mt-0.5 text-xs text-muted-foreground">Add a payment method.</div>
            </div>
          </div>

          <div
            className={`${showCheckout ? "min-h-0 flex-1 overflow-y-auto" : ""} px-4 py-4`}
            style={{
              WebkitOverflowScrolling: "touch",
              overscrollBehavior: "contain",
              touchAction: "pan-y",
            }}
            ref={contentAutoAnimateRef}
          >
            {!showCheckout ? (
              <div className="space-y-2">
                {existingPayment ? (
                  <div className="rounded-xl border border-input bg-muted/20 px-3 py-2">
                    <div className="text-xs font-medium text-muted-foreground">Current Payment Method</div>
                    <div className="ph-mask mt-1 text-sm">{existingSummary}</div>
                  </div>
                ) : null}

                <Button type="button" className="w-full" onClick={() => setShowCheckout(true)} data-testid="payment-edit">
                  {existingPayment ? "Change payment method" : "Add payment method"}
                </Button>

                {saveChoiceUiEnabled && existingPayment && summaryChoice === "removed" ? (
                  <div className="pt-3 pb-1 text-center text-xs text-muted-foreground" data-testid="save-billing-removed">
                    We won't save this card for future messages.{" "}
                    <button
                      type="button"
                      className="underline underline-offset-2 disabled:opacity-50"
                      onClick={() => void setSummarySave(true)}
                      disabled={summaryBusy}
                      data-testid="save-billing-undo"
                    >
                      Undo
                    </button>
                  </div>
                ) : null}
                {saveChoiceUiEnabled && existingPayment && summaryChoice !== "removed" && summarySaved ? (
                  <div className="pt-3 pb-1 text-center">
                    <button
                      type="button"
                      className="text-xs text-muted-foreground underline underline-offset-2 disabled:opacity-50"
                      onClick={() => void setSummarySave(false)}
                      disabled={summaryBusy}
                      data-testid="save-billing-optout"
                    >
                      Don't save this card for future messages
                    </button>
                  </div>
                ) : null}
              </div>
            ) : null}

            {showCheckout ? (
              <div className="relative min-h-[260px] w-full">
                {!embeddedMounted && !loadError ? (
                  <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                    <div className="flex items-center gap-2 rounded-xl border bg-background/90 px-3 py-2 text-sm text-muted-foreground shadow-sm">
                      <RefreshCwIcon className="size-4 animate-spin" aria-hidden="true" />
                      <span>Loading checkout…</span>
                    </div>
                  </div>
                ) : null}
                <div
                  ref={mountRef}
                  data-testid="payment-embedded-checkout"
                  className={`ph-no-capture ${!embeddedMounted ? "opacity-0" : ""}`}
                />
              </div>
            ) : null}

            {loadError ? <div className="ph-mask mt-3 text-xs text-destructive">{loadError}</div> : null}
          </div>

          <div className="flex items-center justify-between gap-3 border-t px-4 py-3">
            {saveChoiceUiEnabled && showCheckout ? (
              <label className="flex min-w-0 cursor-pointer items-center gap-2 text-xs text-muted-foreground">
                <input
                  type="checkbox"
                  className="size-4 shrink-0 accent-primary"
                  checked={saveCard}
                  onChange={(e) => {
                    const next = e.target.checked;
                    saveCardRef.current = next;
                    setSaveCard(next);
                    if (sessionIdRef.current) storeSaveBillingChoice(sessionIdRef.current, next);
                  }}
                  data-testid="save-billing-checkbox"
                />
                <span>Save this card for my messages &amp; photos</span>
              </label>
            ) : (
              <span />
            )}
            <DocumentDialogClose asChild>
              <Button type="button" variant="ghost" disabled={isBusy}>
                Cancel
              </Button>
            </DocumentDialogClose>
          </div>
        </div>
      </DocumentDialogContent>
    </DocumentDialog>
  );
}
